import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Paper, Typography } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { ace } from "./theme";
import CustomerDetailsStep from "./steps/CustomerDetailsStep";
import OriginAddressStep from "./steps/OriginAddressStep";
import DestinationAddressStep from "./steps/DestinationAddressStep";
import InventoryStep, { buildPayloadItem } from "./steps/InventoryStep";
import ReviewSubmitStep from "./steps/ReviewSubmitStep";
import CustomerInventoryPDF from "../CustomerInventoryPDF/CustomerInventoryPDF";
import { getPublicQuotation, patchPublicQuotation, submitPublicQuotation } from "../../services/publicQuotationApi";
import { getTenantBrandingBySlug } from "../../services/publicBrandingApi";
import { getPublicTenantSlug } from "../../config/apiBase";
import { validatePublicWizardStep } from "../../utils/publicWizardValidation";

const STEP_LABELS = ["Your details", "Origin", "Destination", "Inventory", "Review"];

const initialForm = () => ({
  customerName: "",
  email: "",
  phoneNumber: "",
  companyName: "",
  preferredMovingDate: "",
  notes: "",
  originAddress1: "",
  originAddress2: "",
  originCity: "",
  originState: "",
  originPincode: "",
  originCountry: "",
  destinationAddress1: "",
  destinationAddress2: "",
  destinationCity: "",
  destinationState: "",
  destinationPincode: "",
  destinationCountry: "",
});

function rowsFromApiItems(items) {
  if (!items || !items.length) return [{ id: 1, item: "", quantity: "1", normalizedItem: "" }];
  return items.map((it, i) => ({
    id: i + 1,
    item: it.itemName || "",
    quantity: String(it.quantity ?? ""),
    normalizedItem: it.normalizedItemName || "",
  }));
}

const useDebouncedCallback = (fn, delayMs) => {
  const t = useRef(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  return useCallback((...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => fnRef.current(...args), delayMs);
  }, [delayMs]);
};

const PublicQuotationWizard = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [rows, setRows] = useState([{ id: 1, item: "", quantity: "1", normalizedItem: "" }]);
  const [vehicles, setVehicles] = useState([""]);
  const [submitted, setSubmitted] = useState(false);
  const [thankYou, setThankYou] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stepValidationError, setStepValidationError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [brand, setBrand] = useState({ companyName: "Ace Relocations", primaryColor: ace.red });
  const [signerName, setSignerName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const signatureRef = useRef(null);

  const applyQuotationToState = (quotation, items) => {
    setForm({
      customerName:
        quotation.customerName && quotation.customerName !== "Pending"
          ? quotation.customerName
          : "",
      email: quotation.email || "",
      phoneNumber: quotation.phoneNumber || "",
      companyName: quotation.companyName || "",
      preferredMovingDate: quotation.preferredMovingDate || "",
      notes: quotation.notes || "",
      originAddress1: quotation.originAddress1 || "",
      originAddress2: quotation.originAddress2 || "",
      originCity: quotation.originCity || "",
      originState: quotation.originState || "",
      originPincode: quotation.originPincode || "",
      originCountry: quotation.originCountry || "",
      destinationAddress1: quotation.destinationAddress1 || "",
      destinationAddress2: quotation.destinationAddress2 || "",
      destinationCity: quotation.destinationCity || "",
      destinationState: quotation.destinationState || "",
      destinationPincode: quotation.destinationPincode || "",
      destinationCountry: quotation.destinationCountry || "",
    });
    const r = rowsFromApiItems(items);
    setRows(r);
    const v = quotation.vehicles;
    setVehicles(Array.isArray(v) && v.length ? v.map((x) => String(x)) : [""]);
    const custSub = !!quotation.customerSubmitted;
    setSubmitted(custSub);
    setThankYou(custSub);
    const st = Number(quotation.publicWizardStep);
    if (custSub) setStep(4);
    else if (Number.isFinite(st) && st >= 0 && st <= 4) setStep(st);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setLoadError("Invalid link.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError("");
      try {
        const [quoteRes, brandRes] = await Promise.allSettled([
          getPublicQuotation(token),
          getTenantBrandingBySlug(getPublicTenantSlug()),
        ]);

        if (cancelled) return;

        if (quoteRes.status === "fulfilled") {
          const { quotation, items } = quoteRes.value.data;
          applyQuotationToState(quotation, items);
        } else {
          setLoadError(
            quoteRes.reason?.response?.data?.message || "We couldn’t open this link. It may be invalid."
          );
        }

        if (brandRes.status === "fulfilled" && brandRes.value?.data) {
          setBrand((prev) => ({ ...prev, ...brandRes.value.data }));
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err?.response?.data?.message || "We couldn’t open this link. It may be invalid.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const persist = useCallback(async (body, mode = "debounced") => {
    if (!token || submitted) return;
    if (mode === "debounced") setSaveState("saving");
    try {
      await patchPublicQuotation(token, body);
      if (mode === "debounced") setSaveState("saved");
    } catch (err) {
      if (mode === "debounced") setSaveState("error");
    }
  }, [token, submitted]);

  const buildPatchBody = useCallback((stepIndex, formSlice, rowsSlice, vehiclesSlice) => {
    const itemsPayload = rowsSlice.map(buildPayloadItem).filter(Boolean);
    const vehicleList = Array.isArray(vehiclesSlice)
      ? vehiclesSlice.map((x) => String(x).trim()).filter(Boolean)
      : [];
    return {
      ...formSlice,
      publicWizardStep: stepIndex,
      items: itemsPayload,
      vehicles: vehicleList,
    };
  }, []);

  const draftRef = useRef({ form, rows, vehicles, step });
  draftRef.current = { form, rows, vehicles, step };

  const debouncedPersist = useDebouncedCallback(() => {
    const { form: f, rows: r, vehicles: v, step: s } = draftRef.current;
    persist(buildPatchBody(s, f, r, v), "debounced");
  }, 700);

  useEffect(() => {
    if (loading || loadError || submitted) return;
    debouncedPersist();
  }, [form, rows, vehicles, step, loading, loadError, submitted, debouncedPersist]);

  useEffect(() => {
    setStepValidationError("");
  }, [form, rows, vehicles]);

  const handleFieldChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const vehicleListForPdf = useCallback(() => vehicles.map((x) => String(x).trim()).filter(Boolean), [vehicles]);

  const handleDownloadInventoryPdf = useCallback(async () => {
    const itemsForPdf = rows.map(buildPayloadItem).filter(Boolean);
    if (!itemsForPdf.length && !thankYou) {
      setStepValidationError("Add at least one inventory line with quantity before downloading.");
      return;
    }
    setPdfLoading(true);
    try {
      const blob = await pdf(
        <CustomerInventoryPDF
          customer={form}
          items={itemsForPdf.map((i) => ({ itemName: i.itemName, quantity: i.quantity }))}
          vehicles={vehicleListForPdf()}
        />
      ).toBlob();
      const safe = (form.customerName || "move-inventory").replace(/[^\w-]+/g, "_").slice(0, 80);
      saveAs(blob, `ACE-move-inventory-${safe}.pdf`);
    } finally {
      setPdfLoading(false);
    }
  }, [form, rows, vehicleListForPdf, thankYou]);

  const goNext = async () => {
    if (submitted) return;
    const err = validatePublicWizardStep(step, form, rows);
    if (err) {
      setStepValidationError(err);
      return;
    }
    setStepValidationError("");
    setSaveState("saving");
    try {
      const next = Math.min(step + 1, 4);
      await patchPublicQuotation(token, buildPatchBody(next, form, rows, vehicles));
      setStep(next);
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
    }
  };

  const goBack = async () => {
    if (submitted) return;
    setStepValidationError("");
    const prev = Math.max(step - 1, 0);
    setSaveState("saving");
    try {
      await patchPublicQuotation(token, buildPatchBody(prev, form, rows, vehicles));
      setStep(prev);
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
    }
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setStepValidationError("");
    for (let s = 0; s <= 3; s += 1) {
      const vErr = validatePublicWizardStep(s, form, rows);
      if (vErr) {
        setStep(s);
        setStepValidationError(vErr);
        return;
      }
    }
    const sig = signatureRef.current?.getDataUrl?.() || "";
    const nameTrim = String(signerName || "").trim();
    if (nameTrim.length < 2) {
      setSubmitError("Please enter your full name exactly as signed.");
      return;
    }
    if (!acceptedTerms) {
      setSubmitError("Please confirm that the information is accurate before submitting.");
      return;
    }
    if (!sig.startsWith("data:image/png;base64,")) {
      setSubmitError("Please draw your signature in the signature box.");
      return;
    }
    setSubmitting(true);
    setSaveState("saving");
    try {
      await patchPublicQuotation(token, buildPatchBody(4, form, rows, vehicles));
      const res = await submitPublicQuotation(token, {
        signerName: nameTrim,
        signatureDataUrl: sig,
        acceptedTerms: true,
      });
      setSubmitted(true);
      setThankYou(true);
      setStep(4);
      setSaveState("saved");
      if (res.data?.data?.quotation) {
        applyQuotationToState(res.data.data.quotation, res.data.data.items || []);
      }
    } catch (err) {
      setSubmitError(err?.response?.data?.message || "Could not submit. Please check the form.");
      setSaveState("error");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / STEP_LABELS.length) * 100;

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        style={{ background: ace.grayBg }}
      >
        <CircularProgress style={{ color: ace.red }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        style={{ background: ace.grayBg, padding: 24 }}
      >
        <Paper style={{ maxWidth: 480, padding: 32, borderRadius: 16, textAlign: "center" }}>
          <Typography variant="h6" style={{ color: ace.black, marginBottom: 12 }}>
            Link not available
          </Typography>
          <Typography variant="body2" style={{ color: ace.muted }}>
            {loadError}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const itemsPreview = rows.map(buildPayloadItem).filter(Boolean);

  return (
    <div style={{ minHeight: "100vh", background: ace.grayBg, fontFamily: '"Inter", "Roboto", sans-serif' }}>
      <header
        style={{
          background: ace.black,
          color: ace.white,
          padding: "20px 24px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <Typography
            variant="h5"
            component="span"
            style={{ fontWeight: 800, letterSpacing: "-0.02em", color: brand.primaryColor || ace.red, display: "block" }}
          >
            {brand.companyName || "Ace Relocations"}
          </Typography>
          <Typography variant="body2" style={{ opacity: 0.88, marginTop: 8, color: ace.white }}>
            Tell us about your move and inventory. We&apos;ll prepare your quotation.
          </Typography>
        </div>
      </header>

      <div style={{ maxWidth: 880, margin: "0 auto", padding: "24px 16px 80px" }}>
        <Paper
          elevation={0}
          style={{
            borderRadius: 16,
            padding: "8px 0 0",
            overflow: "hidden",
            border: `1px solid ${ace.grayBorder}`,
          }}
        >
          <div style={{ padding: "8px 20px 0" }}>
            <div
              style={{
                height: 6,
                borderRadius: 3,
                background: ace.grayBorder,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: 6,
                  borderRadius: 3,
                  background: ace.red,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} mb={2}>
              <Typography variant="caption" style={{ color: ace.muted }}>
                Step {step + 1} of {STEP_LABELS.length}
              </Typography>
              <Typography variant="caption" style={{ color: ace.muted }}>
                {saveState === "saving" && "Saving…"}
                {saveState === "saved" && !submitted && "Saved"}
                {saveState === "error" && "Could not save — check connection"}
                {saveState === "idle" && !submitted && "\u00a0"}
              </Typography>
            </Box>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
              {STEP_LABELS.map((label, i) => (
                <span
                  key={label}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    background: i === step ? (brand.primaryColor || ace.red) : ace.grayBg,
                    color: i === step ? ace.white : ace.muted,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
            {stepValidationError ? (
              <Typography variant="body2" style={{ color: brand.primaryColor || ace.red, marginBottom: 12, fontWeight: 500 }}>
                {stepValidationError}
              </Typography>
            ) : null}
          </div>

          <div style={{ padding: "8px 24px 28px" }}>
            {step === 0 && (
              <CustomerDetailsStep value={form} onChange={handleFieldChange} submitted={submitted} />
            )}
            {step === 1 && (
              <OriginAddressStep value={form} onChange={handleFieldChange} submitted={submitted} />
            )}
            {step === 2 && (
              <DestinationAddressStep value={form} onChange={handleFieldChange} submitted={submitted} />
            )}
            {step === 3 && (
              <InventoryStep
                rows={rows}
                setRows={setRows}
                vehicles={vehicles}
                setVehicles={setVehicles}
                submitted={submitted}
              />
            )}
            {step === 4 && (
              <ReviewSubmitStep
                value={form}
                items={itemsPreview.length ? itemsPreview : rows.map(buildPayloadItem).filter(Boolean)}
                vehicles={vehicleListForPdf()}
                thankYou={thankYou}
                submitted={submitted}
                signerName={signerName}
                setSignerName={setSignerName}
                acceptedTerms={acceptedTerms}
                setAcceptedTerms={setAcceptedTerms}
                signatureRef={signatureRef}
                onDownloadPdf={handleDownloadInventoryPdf}
                pdfLoading={pdfLoading}
              />
            )}

            {submitError && (
              <Typography variant="body2" style={{ color: ace.red, marginTop: 16 }}>
                {submitError}
              </Typography>
            )}
          </div>
        </Paper>

        {!thankYou && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{
              marginTop: 20,
              position: "sticky",
              bottom: 0,
              background: ace.grayBg,
              paddingTop: 12,
              paddingBottom: 8,
            }}
          >
            <Button
              startIcon={<ChevronLeftIcon />}
              onClick={goBack}
              disabled={step === 0 || submitting}
              style={{ color: ace.charcoal }}
            >
              Back
            </Button>
            {step < 4 ? (
              <Button
                variant="contained"
                endIcon={<ChevronRightIcon />}
                onClick={goNext}
                disabled={submitting}
                style={{
                  background: `linear-gradient(135deg, ${brand.primaryColor || ace.red}, ${ace.redDark})`,
                  color: ace.white,
                  fontWeight: 600,
                  padding: "10px 24px",
                  boxShadow: "0 8px 24px rgba(196, 30, 58, 0.35)",
                }}
              >
                Continue
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || submitted}
                style={{
                  background: `linear-gradient(135deg, ${brand.primaryColor || ace.red}, ${ace.redDark})`,
                  color: ace.white,
                  fontWeight: 600,
                  padding: "10px 24px",
                  boxShadow: "0 8px 24px rgba(196, 30, 58, 0.35)",
                }}
              >
                {submitting ? <CircularProgress size={22} color="inherit" /> : "Submit request"}
              </Button>
            )}
          </Box>
        )}
      </div>
    </div>
  );
};

export default PublicQuotationWizard;
